import React, { useState } from 'react';
import Overlay from '@ynap/overlay';
import BEMHelper from '@ynap/bem-helper';
import { useContentByComponentKey } from '@ynap/content-hook';
import Markdown from 'markdown-to-jsx';
import { ContentByComponentKeyProps, ContentByComponentKeyResponse } from './types/ContentOverlay';
import Portal from '@ynap/portal';

export interface ContentOverlayProps {
    componentKey: string;
    buttonLabel: React.ReactNode;
    titleLabel: React.ReactNode;
    overlayTypeModifier?: string;
    dataTestId?: string;
    modifiers: {
        buttonModifier: string;
        textModifier: string;
    };
    fallbackContent?: React.ReactNode;
}


const bem = new BEMHelper('ContentOverlay');

export const getContentByComponentKey = (contentByComponentKey: ContentByComponentKeyProps) => {
    if (!contentByComponentKey) {
        return { content: undefined };
    }
    return {
        content: contentByComponentKey?.content?.[0]?.teaser?.teaserText,
    };
};

function ContentOverlay({ componentKey, buttonLabel, titleLabel, overlayTypeModifier, dataTestId, modifiers, fallbackContent }: ContentOverlayProps) {
    const [activePortalKey, setActivePortalKey] = useState<string>('');
    const OVERLAY_KEY = 'contentOverlay';

    const openOverlay = () => setActivePortalKey(OVERLAY_KEY);
    const closeOverlay = () => setActivePortalKey('');

    const { content, loading }: ContentByComponentKeyResponse = useContentByComponentKey({
        componentKey,
        normalize: getContentByComponentKey,
    });

    const teaserText = content?.content?.replace(/^\n\n/, '') || '';

    if (loading || (!teaserText && !fallbackContent)) {
        return null;
    }

    return (
        <>
            <button
                className={bem('buttonLabel', modifiers.buttonModifier)}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openOverlay();
                }}
            >
                {buttonLabel}
            </button>

            <Portal show={activePortalKey}>
                <Overlay
                    key={OVERLAY_KEY}
                    onClose={closeOverlay}
                    handleBodyClick={closeOverlay}
                    className={{
                        container: bem('overlay'),
                        content: bem('content'),
                        button: bem('close'),
                    }}
                    title={titleLabel}
                    type={overlayTypeModifier}
                >
                    <div className={bem('text', modifiers.textModifier)}>
                        {teaserText ? (
                            <Markdown
                                options={{ forceInline: true }}
                                className={bem('textBody')}
                                data-testid={dataTestId}
                            >
                                {teaserText}
                            </Markdown>
                        ) : (
                            fallbackContent
                        )}
                    </div>
                </Overlay>
            </Portal>
        </>
    );
}

export default ContentOverlay;
